/** @jsx jsx */
import {
  BaseButton,
  Button,
  Grid,
  Icon,
  IconButton,
  Spacer,
  useClipboard,
  useMediaQuery,
} from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms"
import { H3, H4, P } from "@cd2/text"
import { assert, doo, genId, lightenDarkenColor } from "@cd2/tools"
import { InfoBanner } from "../AlertBanners"
import { css, jsx } from "@emotion/core"
import { add, differenceInDays } from "date-fns"
import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { buildCustomerSearchFilter } from "../../../shared/search"
import { CreateButton } from "../../Components/Buttons/CreateButton"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { ConfirmCancelDialogue } from "../../Components/ConfirmCancelDialogue"
import { useDownload } from "../../Components/DownloadFileButton"
import { brandColors, colors, themeColors } from "../../Components/EOSThemeProvider"
import { useEvents } from "../../Components/EventsList"
import { useEnsureGlobalState, useGlobalState } from "../../Components/GlobalStateProvider"
import { KeyValueList } from "../../Components/KeyValueList"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { QuickToolTipWrapper } from "../../Components/QuickToolTipWrapper"
import { BranchSelect } from "../../Components/SelectLists/BranchSelect"
import { ContactSelectList } from "../../Components/SelectLists/ContactSelectList"
import {
  CustomerSelectList2,
  ICustomerSelect2,
} from "../../Components/SelectLists/CustomerSelectList2"
import { SupportTicketSelect } from "../../Components/SelectLists/SupportTicketSelect"
import { UserSelectList } from "../../Components/SelectLists/UserSelectList"
import Btn from "../../comps/Button"
import { buttonConfigs } from "../../constants/buttonConfigs"
import { icons } from "../../constants/icons"
import { useApi } from "../../hooks/useApi"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useConfirmCancelConfirmation } from "../../hooks/useConfirmCancelConfirmation"
import { useDebouncedState } from "../../hooks/useDebouncedState"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { useStable } from "../../hooks/useStable"
import { HoverCustomerLoader } from "../../hoverLoaders/HoverCustomerLoader"
import { HoverOrderLoader } from "../../hoverLoaders/HoverOrderLoader"
import { HoverProductLoader } from "../../hoverLoaders/HoverProductLoader"
import { HoverSupportTicketLoader } from "../../hoverLoaders/HoverSupportTicketLoader"
import { HoverUserLoader } from "../../hoverLoaders/HoverUserLoader"
import { buildRoute, eRoutes } from "../../routes"
import { AttachmentsField } from "../../stories/quoteOrders/QuotesOrdersForm"
import { fileTypes } from "../../types/common"
import { IRecord } from "../../types/schema"
import { skeem } from "../../utils/skeem"
import { IBranchSettings, useSettings } from "../../utils/useSettings"
import { formatPrice } from "../../utils/utils"
import { AlertBanners } from "../AlertBanners"
import { IORBranchIRecord } from "../Branches/BranchesShowPage"
import { ProductMasterThumbnail } from "../CustomerArea/components/ProductMasterThumbnail"
import { useContactCreateForCustomerConfirmation } from "../Customers/CustomerShowContactsPage"
import { InternalRmaBranchSelect } from "./components/InternalRmaBranchSelect"
import { handleRMASupportTicket } from "./RmaNewPage"
import {
  IRmaAssignedTo,
  IRmaAttachment,
  IRmaBranch,
  IRmaContact,
  IRmaCustomer,
  IRmaItem,
  IRmaItemOrder,
  IRmaOrder,
  IRmaProduct,
  IRmaRecord,
  IRmaSubCustomer,
  IRmaSupportTicket,
  rmaContactAttributes,
  rmaItemOrderAttributes,
  rmaProductAttributes,
  RmaRequestType,
  rmaRequestTypes,
  RmaReturnReason,
  rmaReturnReasons,
  RmaStatus,
  rmaStatusesGrouped,
  rmaSubCustomerAttributes,
} from "./RmasIndexPage"
import { useManagedDialog } from "../../hooks/useManagedDialog"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { FieldsRadioCheckbox } from "../../Components/FieldsRadioCheckbox"

export interface IRmaFormData {
  // association data
  requestType: RmaRequestType
  creditNumber: string
  branch?: IRmaBranch
  items?: IRmaItem[]
  order?: IRmaOrder
  attachments?: IRmaAttachment[]
  customer?: IRmaCustomer
  sub_customer?: IRmaSubCustomer
  contact?: IRmaContact
  assignedTo?: IRecord<"skeem_users">
  supportTicket?: IRmaSupportTicket
  internalRmaBranch?: IORBranchIRecord
  isInternal?: boolean
  created_by?: IRecord<"skeem_users", "name" | "email">

  // attributes
  orderDate: Date
  returnReason: RmaReturnReason
  additionalDetails: string
  technicalCaseNumber: string
  returnsNumber: string
  closedReason: string
  statusMessage: string
  notes: string
  show_notes_on_confirmation?: boolean
  status: (typeof rmaStatusesGrouped)[number]["options"][number]

  // invisible fields // not an actual field but is a real attr
  salesNumber: string
}

export interface IRmaDataFromForm extends Omit<IRmaFormData, "items"> {
  items: Record<string, IRmaItem>
}

export function fakeID() {
  return `temp-${genId()}`
}

export function isFakeID(id: string) {
  return id.startsWith("temp-")
}

function newBaseItem(data?: { comment?: boolean }) {
  return {
    id: fakeID(),
    quantity: 1,
    product: undefined,
    serial_number: "",
    tech_case_number: "",
    item_type: data?.comment ? "comment" : "product",
    comment: "",
  }
}

function filterDeletedItems(items: Record<string, IRmaItem>) {
  return Object.entries(items)
    .filter(x => !x[1]._delete)
    .reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {} as Record<string, IRmaItem>)
}

function filterValidatableItems(items: Record<string, IRmaItem>) {
  return Object.entries(items)
    .filter(x => !x[1]._delete && x[1].item_type !== "comment")
    .reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {} as Record<string, IRmaItem>)
}

export interface IRmaProductRequirement {
  techCaseRequired: boolean
  serialRequired: boolean
}

export const RmaProductRequirementsContext = React.createContext<
  Record<string, IRmaProductRequirement>
>({})

export function useRmaProductRequirement(productId?: string) {
  const requirements = React.useContext(RmaProductRequirementsContext)
  return productId ? requirements[productId] : undefined
}

const closedReasons = ["Outside of warranty", "Incorrect details"]

const toolbarItemWidth = css`
  min-width: 200px;
`

export function BranchRmaForm({
  onSubmit,
  initialData,
  customerView,
  isEditPage,
  createdAt,
  internalView,
  refetch,
  recordId,
}: {
  onSubmit: (data: IRmaFormData) => Promise<any>
  initialData: Partial<IRmaRecord>
  customerView?: boolean
  isEditPage?: boolean
  createdAt?: Date
  internalView?: boolean
  refetch?: () => Promise<void> | void
  recordId?: string
}) {
  const { rmaNotePresets } = useSettings()
  const { currentUser } = useGlobalState()
  const events = useEvents()
  const alerts = useSiteAlerts()
  const api = useApi()
  const [returnNumberRequired, setReturnNumberRequired] = React.useState(false)

  function formatInitialData(initialData: Partial<IRmaRecord>): Partial<IRmaDataFromForm> {
    return {
      ...initialData,
      items: initialData?.items?.reduce((acc, item) => {
        acc[item.id] = item
        return acc
      }, {} as Record<string, IRmaItem>) ?? { ["1"]: newBaseItem() },
      supportTicket: customerView ? undefined : initialData?.supportTicket,
      orderDate: initialData?.orderDate ? new Date(initialData.orderDate) : undefined,
      attachments: initialData?.attachments?.map(attachment => attachment as any) ?? [],
      isInternal: !!initialData?.internalRmaBranch,
      status:
        rmaStatusesGrouped
          .find(x => x.options.find(y => y.value === initialData?.status))
          ?.options.find(y => y.value === initialData?.status) || rmaStatusesGrouped[0].options[0],
    }
  }

  const [contactFormNode, _getContactInformation, handleCreateContactFromNode] =
    useContactCreateForCustomerConfirmation()

  async function handleCreateContact(customer: string) {
    assert(customer)
    const customerRecord = await skeem.fetch2<
      IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> }
    >({
      first: {
        name: "customers",
        attributes: ["name", "branch", "isGenericCustomer"],
        filter: { matches: { id: customer } },
      },
    })
    try {
      const contact = await handleCreateContactFromNode(customerRecord)
      if (contact) {
        return contact.id
      }
    } catch (e) {
      alerts.genericError(JSON.stringify(e))
    }
  }

  const bgColor = customerView && !internalView ? "transparent" : "#e5e5e5"

  console.log("INIT DATA", formatInitialData(initialData))

  const [confirmNewSupportTicket, handleConfirmCreateSupportTicket] = useConfirmCancelConfirmation({
    heading: "Create new support ticket?",
    body: "Are you sure you want to create a new support ticket? This will attempt to build a support ticket using the data supplied in this RMA",
  })
  const [confirmNoTicket, handleConfirmNoTicket] = useConfirmCancelConfirmation({
    heading: "Error saving support ticket",
    body: "Continue without support ticket?",
  })

  async function handleSubmit(data: IRmaDataFromForm) {
    const baseData = { ...data, items: Object.values(data.items) }
    if (baseData.supportTicket?.id === "NEW") {
      if (!(await handleConfirmCreateSupportTicket())) {
        return
      }
      let result: { record?: any; error?: string | null } | undefined = undefined

      try {
        result = await handleRMASupportTicket({ skeem, events, api }, baseData)
      } catch (e) {
        // cancel save if actual error
        console.error(e)
        alerts.genericError(e.message || "Error creating support ticket")
        return
      }

      if (result?.record) {
        // if saved support ticket, save RMA
        return onSubmit({
          ...baseData,
          supportTicket: result.record ? result.record : undefined,
        })
      } else {
        console.error(result)
        // if support ticket didn't save due to missng type, show error and confirm proceed without ticket
        alerts.genericError(result?.error || "Failed to create support ticket")
        if (await handleConfirmNoTicket()) {
          return onSubmit({ ...baseData, supportTicket: undefined })
        }
      }
    } else {
      // normal handle if not creating support ticket
      return onSubmit(baseData)
    }
    return
  }

  async function handleRaiseReturnRequest() {
    const { internalRmaBranch: branchesData, items } = initialData
    try {
      if (!branchesData) {
        throw new Error("No internal RMA branch selected")
      }

      const itemsWithoutComments = items?.filter(item => item.item_type !== "comment") ?? []
      if (
        !itemsWithoutComments ||
        itemsWithoutComments.length === 0 ||
        itemsWithoutComments.some(item => !item.product)
      ) {
        throw new Error("No items in RMA to raise return request for")
      }

      const returnToBranchId = branchesData.targetBranch.id
      const returnFromBranchId = branchesData.sourceBranch.id

      type IOR = IRecord<"orderRequestBranches", "customer"> & {
        shippingAddress: IRecord<
          "addresses",
          | "line_1"
          | "line_2"
          | "line_3"
          | "line_4"
          | "city"
          | "postcode"
          | "country"
          | "county"
          | "name"
          | "contact"
          | "email"
          | "label"
          | "lat"
          | "lng"
          | "phone"
        >
      }
      const orderRequestBranch = await skeem.fetch2<IOR>({
        first: {
          name: "orderRequestBranches",
          attributes: [
            "customer",
            {
              name: "shippingAddress",
              attributes: [
                "line_1",
                "line_2",
                "line_3",
                "line_4",
                "city",
                "postcode",
                "country",
                "county",
                "name",
                "contact",
                "email",
                "label",
                "lat",
                "lng",
                "phone",
              ],
            },
          ],
          matches: { sourceBranch: returnToBranchId, targetBranch: returnFromBranchId },
        },
      })
      if (!orderRequestBranch || !orderRequestBranch.customer) {
        throw new Error("No order request branch found for these branches")
      }

      const branchWithIdentifier = await window.skeem.mutate({
        branches: {
          update: { id: returnFromBranchId, nextOrderIdentifier: { inc: 1 } },
        },
      })
      const indentifier = String(branchWithIdentifier.nextOrderIdentifier).padStart(6, "0")

      const branchItems = await window.skeem.fetch2<
        (IRecord<"products", "stock_code" | "cost_price_pennies"> & {
          product_locations?: IRecord<"product_locations", "name">[]
        })[]
      >({
        model: {
          name: "products",
          matches: { id: itemsWithoutComments.map(item => item.product!.id) },
          attributes: [
            "stock_code",
            {
              name: "product_locations",
              attributes: ["name"],
            },
            "cost_price_pennies",
          ],
        },
      })
      type IMBI = (IRecord<"products", "stock_code" | "cost_price_pennies"> & {
        product_locations?: (IRecord<"product_locations", "name"> & {
          warehouse?: IRecord<"warehouses", "name">
        })[]
      })[]
      const matchingBranchItems = await window.skeem.fetch2<IMBI>({
        model: {
          name: "products",
          matches: {
            stock_code: branchItems.map(item => item.stock_code),
            branch: returnFromBranchId,
          },
          attributes: [
            "stock_code",
            {
              name: "product_locations",
              attributes: ["name", { name: "warehouse", attributes: ["name"] }],
            },
            "cost_price_pennies",
          ],
        },
      })

      const branchObj = await skeem.fetch2<{
        warehouses: IRecord<"warehouses", "name">[]
        branch: { settings: IBranchSettings }
      }>({
        obj: {
          warehouses: {
            model: {
              name: "warehouses",
              attributes: ["name"],
              matches: { branch: returnFromBranchId },
            },
          },
          branch: {
            first: {
              name: "branches",
              attributes: ["settings"],
              matches: { id: returnFromBranchId },
            },
          },
        },
      })

      const defaultLocationName =
        branchObj.branch.settings.defaultStockLocation || branchObj.warehouses[0]?.name
      console.log("DEFAULT LOCATION NAME", defaultLocationName)

      // Could the delivery address be added for the return to go to the requesting branches address?
      const { id: _, ...copyAddress } = { ...(orderRequestBranch.shippingAddress ?? {}) }
      const sortedItems = items
        ?.sort((a, b) => {
          if (!a.created_at || !b.created_at) return 0
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        })
        .filter((item, idx, arr) => {
          if (item.item_type === "comment") return true
          return (
            arr.findIndex(i => i.item_type !== "comment" && i.product?.id === item.product?.id) ===
            idx
          )
        })

      const order = await window.skeem.mutate({
        orders: {
          create: {
            branch: { set: returnFromBranchId },
            type: "return_request",
            delivery_note_email: "rma@fortusuk.com", // harcoded per request
            source_branch_id: returnFromBranchId,
            identifier: indentifier,
            customer: { set: orderRequestBranch.customer.id },
            return_request_source_rma: { set: initialData.id! },
            user: { set: currentUser.id },
            purchase_order_reference: `RMA${initialData.identifier}`,
            contact: { set: initialData.contact?.id ?? null },
            delivery_method: "DPD",
            autoEmailOrderAcknowledgementOn: "onDelivery",
            internal_transfer_to_branch: { set: returnToBranchId },
            address: { create: copyAddress },
            notes: initialData.notes,
            items: sortedItems?.map((item, index) => {
              if (item.item_type === "comment") {
                return {
                  create: {
                    stock_code: "",
                    product_location: { set: null },
                    body: item.comment ?? "",
                    quantity: 1,
                    allocated_quantity: 1,
                    type: 1,
                    weight: index,
                  },
                }
              }
              if (!item.product) {
                throw new Error("Item has no product")
              }
              const branchItem = branchItems.find(bi => bi.stock_code === item.product!.stock_code)
              if (!branchItem) {
                throw new Error(
                  `No product found in branch for stock code ${item.product.stock_code}`,
                )
              }
              const matchingItem = matchingBranchItems.find(
                mi => mi.stock_code === branchItem?.stock_code,
              )
              if (!matchingItem) {
                throw new Error(
                  `No matching product in branch for stock code ${branchItem.stock_code}`,
                )
              }
              const rmaItems = itemsWithoutComments.filter(i => i.product!.id === item.product!.id)
              const totalQty = rmaItems.reduce((acc, i) => acc + (i.quantity ?? 0), 0)
              const locations = matchingItem.product_locations || []
              const location =
                locations.find(
                  loc =>
                    loc.warehouse?.name === defaultLocationName || loc.name == defaultLocationName,
                ) || locations[0]
              return {
                create: {
                  product: { set: matchingItem?.id ?? null },
                  quantity: totalQty,
                  stock_code: branchItem.stock_code,
                  product_location: { set: location?.id },
                  price_pennies: branchItem.cost_price_pennies,
                  cost_price_pennies: branchItem.cost_price_pennies,
                  weight: index,
                },
              }
            }),
          },
        },
      })
      const notificationEmail = initialData.created_by?.email
      if (notificationEmail) {
        await api.sendNotification({
          to: { emails: [notificationEmail] },
          content: {
            heading: `A Return Request has been created for RMA ${initialData.identifier}`,
            body: `A return request has been created for RMA ${initialData.identifier} by ${currentUser.name}.`,
          },
        })
      }
      if (refetch) {
        await refetch()
      }
      alerts.genericSuccess(`Return request created: ${order.identifier}`)
    } catch (error) {
      console.error("Error raising return request:", error)
      alerts.genericError(`Failed to raise return request: ${error.message || error}`)
    }
  }

  const StatusMessageDialog = React.useCallback(
    ({ data, onResolve }: { data: string; onResolve: (s: string) => void }) => {
      const [message, setMessage] = React.useState(data)
      const inputRef = React.useRef<HTMLTextAreaElement>(null)

      React.useEffect(() => {
        inputRef.current?.focus()
      }, [])

      return (
        <ConfirmCancelDialogue
          heading="Set status message"
          onOpenAutoFocus={() => {
            inputRef.current?.focus()
          }}
          body={
            <React.Fragment>
              <P blockSpacing={0}>This message is visible to the customer.</P>
              <Spacer />
              <Fields.TextArea
                ref={inputRef}
                value={message}
                onChange={setMessage}
                rows={8}
                placeholder="Enter status message"
              />
            </React.Fragment>
          }
          buttonOpts={{ confirm: { children: "Submit" } }}
          onCancel={() => onResolve(data)}
          onConfirm={() => onResolve(message)}
          isOpen
        />
      )
    },
    [],
  )

  const [changeStatusMessageModal, handleChangeStatusMessage] = useConfirmation<string, string>(
    StatusMessageDialog,
  )
  return (
    <React.Fragment>
      {contactFormNode}
      {confirmNewSupportTicket}
      {confirmNoTicket}
      {changeStatusMessageModal}
      <Form2.Form
        onSubmit={handleSubmit}
        initialData={formatInitialData(initialData)}
        onError={console.error}
      >
        <ValidateRmaForm setReturnNumberRequired={setReturnNumberRequired} createdAt={createdAt}>
          <div
            css={css`
              display: flex;
              align-items: center;
              padding: 0 16px;
              gap: 16px;
            `}
          >
            {initialData.return_request_order ? (
              <div
                css={css`
                  flex: 1;
                `}
              >
                <div
                  css={css`
                    border: 1px solid ${brandColors.lighterNight};
                    padding: 12px;
                    display: flex;
                    max-width: 700px;
                  `}
                >
                  <div
                    css={css`
                      flex: 1;
                    `}
                  >
                    A Return Request has been created for this RMA:
                    <br />
                    <b>
                      <Link
                        to={buildRoute(eRoutes["/goods-receipts/:id"], {
                          id: initialData.return_request_order.id,
                        })}
                      >
                        {initialData.return_request_order.identifier}
                      </Link>
                    </b>
                  </div>
                  {process.env.NODE_ENV !== "production" && (
                    <div
                      css={css`
                        margin-left: 12px;
                      `}
                    >
                      <Btn
                        label="Regenerate: DEV ONLY"
                        onClick={handleRaiseReturnRequest}
                        variant="secondary"
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : initialData.internalRmaBranch &&
              [RmaStatus.RAISED, RmaStatus.AWAITING_GOODS_RECEIPT].includes(
                initialData.status as any,
              ) ? (
              <div
                css={css`
                  flex: 1;
                `}
              >
                <div
                  css={css`
                    border: 1px solid ${brandColors.lighterNight};
                    padding: 12px;
                    display: flex;
                    max-width: 700px;
                  `}
                >
                  <div
                    css={css`
                      flex: 1;
                    `}
                  >
                    This is an internal RMA for branch{" "}
                    {initialData.internalRmaBranch.targetBranch.name}
                    .<br />
                    <b>Raise a return request?</b>
                  </div>
                  <Btn
                    label="Create Return Request"
                    onClick={handleRaiseReturnRequest}
                    variant="secondary"
                  />
                </div>
              </div>
            ) : (
              <div
                css={css`
                  flex: 1;
                `}
              />
            )}

            {recordId && initialData.status && (
              <div>
                <SendRmaEmail
                  rmaId={recordId}
                  defaultStatus={mapRmaStatusToNotificationStatus(initialData.status)}
                />
              </div>
            )}

            <div
              css={css`
                max-width: 200px;
                margin-left: auto;
              `}
            >
              <Form2.WithForm
                render={form => (
                  <BaseButton
                    {...buttonConfigs.formSaveButton}
                    disabled={form.state.isSubmitting}
                  />
                )}
                subscribe={{ dataChange: true, submitting: true }}
              />
            </div>
          </div>
          <Form2.WithForm
            subscribe={{ dataChange: true, submitting: true }}
            render={form => {
              const branchField = Form2.useField<IRmaBranch | undefined>("branch", {
                subscribe: true,
              })
              const itemsField = Form2.useField<Record<string, IRmaItem>>("items", {
                defaultValue: {},
                subscribe: true,
              })
              const orderField = Form2.useField<IRmaOrder | undefined>("order", { subscribe: true })
              const attachmentsField = Form2.useField<IRmaAttachment[] | undefined>("attachments", {
                subscribe: true,
                defaultValue: [],
              })
              const customerField = Form2.useField<IRmaCustomer | undefined>("customer", {
                subscribe: true,
              })
              const returnReasonField = Form2.useField<RmaReturnReason | undefined>(
                "returnReason",
                {
                  subscribe: true,
                },
              )
              const subCustomerField = Form2.useField<IRmaCustomer | undefined>("sub_customer", {
                subscribe: true,
              })
              const contactField = Form2.useField<IRmaContact | undefined>("contact", {
                subscribe: true,
              })
              const assignedToField = Form2.useField<IRmaAssignedTo | undefined>("assignedTo", {
                subscribe: true,
              })
              const supportTicketField = Form2.useField<IRmaSupportTicket | undefined>(
                "supportTicket",
                {
                  subscribe: true,
                },
              )
              const isReceivingBranch =
                !customerView && currentUser?.branch?.id === branchField.getValue()?.id

              const saveErrors = Form2.useField("saveErrors", { subscribe: true })
              const productRequirements = React.useContext(RmaProductRequirementsContext)

              const [customerOption, setCustomerOption] = useState<ICustomerSelect2 | null>(null)

              useAsyncEffect(async () => {
                if (!customerField.getValue()) {
                  setCustomerOption(null)
                  return
                }
                const record = await skeem.fetch2<ICustomerSelect2>({
                  first: {
                    name: "customers",
                    attributes: CustomerSelectList2.attributes,
                    filter: { matches: { id: customerField.getValue()?.id } },
                  },
                })
                setCustomerOption(record)
                if (record.branch) {
                  form.getField("branch").setValue(record.branch)
                }
              }, [customerField.getValue()])

              return (
                <div
                  css={
                    internalView
                      ? css`
                          padding: 0 16px;
                          max-width: 800px;
                        `
                      : undefined
                  }
                >
                  {(!customerView || internalView) && (
                    <React.Fragment>
                      {internalView && (
                        <InternalRmaBranchSelect
                          branchId={currentUser.branch.id}
                          disabled={isEditPage}
                        />
                      )}
                      <Grid
                        columns={["auto", { min: 180, max: "auto" }]}
                        gap={8}
                        css={css`
                          background-color: ${internalView ? "white" : bgColor};
                          padding: 8px 16px;
                        `}
                      >
                        {!internalView && (
                          <ButtonsWrapper>
                            <React.Fragment>
                              <div css={toolbarItemWidth}>
                                <BranchSelect
                                  label="Branch"
                                  onChange={branchField.setValue}
                                  value={branchField.getValue()}
                                  error={branchField.getError()}
                                  disabled={!!customerField.getValue()}
                                  onlyAccessibleBranches
                                />
                              </div>
                              <div css={toolbarItemWidth}>
                                <ValidateCustomerPresence />
                                <CustomerSelectList2
                                  key={branchField.getValue()?.id || "customer-select"}
                                  label="Customer"
                                  validations={{ presence: true }}
                                  onChange={(_, opt) => {
                                    customerField.setValue(opt)
                                    contactField.setValue(undefined)
                                    subCustomerField.setValue(undefined)
                                  }}
                                  value={customerOption}
                                  branchId={branchField.getValue()?.id}
                                  searchFilter={(query: string) => ({
                                    or: [
                                      buildCustomerSearchFilter(query),
                                      {
                                        exists: {
                                          path: [
                                            {
                                              name: "addresses",
                                              filter: {
                                                and: [
                                                  {
                                                    matches: {
                                                      showInCustomerAddressOptions: true,
                                                    },
                                                  },
                                                  {
                                                    like: [
                                                      {
                                                        stringReplace: [
                                                          { attr: "postcode" },
                                                          " ",
                                                          "",
                                                        ],
                                                      },
                                                      { value: query.replace(/\s/g, "") },
                                                    ],
                                                  },
                                                ],
                                              },
                                            },
                                          ],
                                        },
                                      },
                                    ],
                                  })}
                                  isClearable
                                  error={customerField.getError()}
                                  includeLastOrderDate
                                />
                              </div>

                              <RmaSubCustomerSelectList customerId={customerField.getValue()?.id} />

                              <div css={toolbarItemWidth}>
                                {customerField.getValue() ? (
                                  <Grid
                                    gap={8}
                                    alignItems="end"
                                    columns={[{ fr: 1 }, { fr: 1 }]}
                                  >
                                    <ContactSelectList
                                      label="Contact"
                                      onChange={(_, contact) => contactField.setValue(contact)}
                                      value={contactField.getValue()?.id}
                                      branchId={branchField.getValue()?.id}
                                      customerId={customerField.getValue()?.id}
                                      subCustomerId={subCustomerField.getValue()?.id}
                                      error={contactField.getError()}
                                      skeemFilter={{ not: { matches: { email: "" } } }}
                                      isClearable
                                    />
                                    <div>
                                      <CreateButton
                                        onClick={() => {
                                          handleCreateContact(
                                            customerField.getValue()?.id ?? "",
                                          ).then(async result => {
                                            if (result) {
                                              const contact = await skeem.fetch2<IRmaContact>({
                                                first: {
                                                  name: "contacts",
                                                  attributes: rmaContactAttributes,
                                                  matches: { id: result },
                                                },
                                              })
                                              contactField.setValue(contact)
                                            }
                                          })
                                        }}
                                        buttonStyle="text"
                                        extendTheme={{ padding: 8, fontSize: 12 }}
                                        disabled={!customerField.getValue()}
                                        color="#666"
                                      >
                                        Create new contact
                                      </CreateButton>
                                    </div>
                                  </Grid>
                                ) : (
                                  <Fields.Select options={[]} label="Contact" disabled />
                                )}
                              </div>

                              {initialData.created_by && (
                                <div css={toolbarItemWidth}>
                                  <Label>Created by</Label>
                                  <div
                                    css={css`
                                      font-size: 13px;
                                      color: #4a4e69;
                                      border: 1px solid #bbb;
                                      padding: 4px 8px;
                                      border-radius: 4px;
                                    `}
                                  >
                                    <b>{initialData.created_by.name}</b>
                                    <br />
                                    {initialData.created_by.email}
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          </ButtonsWrapper>
                        )}
                        {!internalView && (
                          <div
                            css={css`
                              display: flex;
                              flex-direction: column;
                              gap: 4px;
                            `}
                          >
                            <Grid
                              columns={["max-content", { fr: 1 }]}
                              gap={8}
                              alignItems="end"
                              css={css`
                                width: 100%;
                              `}
                            >
                              <Form2.WithField<string>
                                field="statusMessage"
                                subscribe={true}
                                render={field => (
                                  <QuickToolTipWrapper
                                    showToolTip={!!field.getValue()}
                                    content={
                                      <span
                                        css={css`
                                          white-space: pre-wrap;
                                        `}
                                      >
                                        {field.getValue()}
                                      </span>
                                    }
                                  >
                                    <Button
                                      onClick={async () => {
                                        const message = await handleChangeStatusMessage(
                                          field.getValue(),
                                        )
                                        field.setValue(message)
                                      }}
                                    >
                                      <span
                                        css={css`
                                          ${field.getValue()
                                            ? "border-bottom: 1px dotted white;"
                                            : ""}
                                        `}
                                      >
                                        {field.getValue() ? "Edit" : "Set"} Message
                                      </span>
                                    </Button>
                                  </QuickToolTipWrapper>
                                )}
                              />
                              <Form2.Select
                                field="status"
                                options={rmaStatusesGrouped}
                                validations={{ presence: true }}
                              />
                            </Grid>
                            <div>
                              <Form2.WithField<{ value: RmaStatus }>
                                field="status"
                                subscribe={true}
                                render={field => {
                                  if (
                                    field.getValue().value == RmaStatus.CLOSED ||
                                    field.getValue().value == RmaStatus.REJECTED
                                  ) {
                                    return (
                                      <Form2.Select
                                        field="closedReason"
                                        label="Reason"
                                        options={closedReasons.map(x => ({ value: x }))}
                                        getOptionLabel={opt => opt?.value}
                                        getOptionValue={opt => opt?.value}
                                        valueExtractor={(opt: { value: string }) => opt?.value}
                                        isClearable
                                      />
                                    )
                                  }
                                  return null
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </Grid>

                      {!internalView && (
                        <Form2.WithField
                          field="status"
                          subscribe={true}
                          render={field => {
                            const message = doo(() => {
                              const was = initialData?.status
                              const is = field.getValue()
                              if (
                                was !== RmaStatus.AWAITING_GOODS_RECEIPT &&
                                is === RmaStatus.AWAITING_GOODS_RECEIPT
                              ) {
                                return "The customer for this RMA will be sent the approval notification on save"
                              }
                              if (was !== RmaStatus.REJECTED && is === RmaStatus.REJECTED) {
                                return "The customer for this RMA will be sent the rejection notification on save"
                              }
                              return null
                            })
                            if (!message) {
                              return null
                            }
                            return (
                              <div
                                css={css`
                                  padding: 4px;
                                `}
                              >
                                <AlertBanners
                                  banners={[
                                    message && {
                                      message: message,
                                      type: "info" as const,
                                    },
                                  ].filter(Boolean)}
                                />
                              </div>
                            )
                          }}
                        />
                      )}
                    </React.Fragment>
                  )}
                  <Grid
                    columns={customerView ? undefined : [{ fr: 1 }, { fr: 2 }, { fr: 1 }]}
                    css={css`
                      background-color: ${bgColor};
                    `}
                  >
                    {!customerView && (
                      <div
                        css={css`
                          background-color: #f5f5f5;
                          padding: 12px;
                        `}
                      >
                        <Grid gap={8}>
                          <FloatingBox>
                            <Grid gap={8}>
                              <UserSelectList
                                label="Assigned To"
                                onChange={(_, user) => assignedToField.setValue(user)}
                                value={assignedToField.getValue()?.id}
                                error={assignedToField.getError()}
                                filter={{ matches: { userAccountType: "branch" } }}
                                isClearable
                              />

                              <SupportTicketSelect
                                label="Support Ticket"
                                onChange={(_, ticket) => supportTicketField.setValue(ticket)}
                                value={supportTicketField.getValue()?.id}
                                branchId={branchField.getValue()?.id}
                                customerId={customerField.getValue()?.id}
                                error={supportTicketField.getError()}
                                isClearable
                              />
                            </Grid>
                          </FloatingBox>

                          {assignedToField.getValue() && (
                            <HoverUserLoader
                              resourceName="Assigned To"
                              id={assignedToField.getValue()!.id}
                              inline
                            />
                          )}

                          {supportTicketField.getValue()?.id &&
                            supportTicketField.getValue()?.id !== "NEW" && (
                              <HoverSupportTicketLoader
                                resourceName="Support Ticket"
                                id={supportTicketField.getValue()!.id}
                                inline
                              />
                            )}
                        </Grid>

                        <Spacer />
                        <Form2.WithField<string>
                          field="notes"
                          render={field => (
                            <FloatingBox
                              header="Notes"
                              headerRightNode={
                                <Form2.Checkbox
                                  field="show_notes_on_confirmation"
                                  label="Include on RMA confirmation"
                                />
                              }
                            >
                              <Fields.Select2
                                label="Apply a preset"
                                onChange={x => {
                                  const currentValue = field.getValue() || ""
                                  if (
                                    !currentValue ||
                                    confirm("Are you sure you want to overwrite the current notes?")
                                  ) {
                                    field.setValue(x.label)
                                  }
                                }}
                                value={null}
                                options={rmaNotePresets.map(x => ({
                                  label: x.value,
                                  value: x.value,
                                }))}
                              />
                              <Spacer />
                              <Form2.TextArea
                                field="notes"
                                rows={8}
                                placeholder="Enter internal notes about this RMA..."
                              />
                            </FloatingBox>
                          )}
                        />
                      </div>
                    )}

                    <div>
                      <Grid
                        gap={16}
                        css={css`
                          background-color: white;
                          padding: ${internalView ? 0 : 16}px;
                        `}
                      >
                        <Grid gap={8} columns={[{ fr: 1 }, { fr: 1 }]}>
                          <OrderLookupField customerView={customerView} />
                          <Form2.Date
                            field="orderDate"
                            label={<Label optional>Order Date</Label>}
                            placeholderText="01/01/2025"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                        </Grid>
                        <Grid gap={8} columns={[{ fr: 1 }, { fr: 1 }]}>
                          <Form2.Select
                            field="returnReason"
                            options={rmaReturnReasons}
                            valueExtractor={(opt: { value: RmaReturnReason }) => opt.value}
                            validations={{ presence: true }}
                          />
                          {!internalView && (
                            <Form2.Select
                              field="requestType"
                              options={rmaRequestTypes(true)}
                              valueExtractor={(opt: { value: RmaRequestType }) => opt.value}
                              validations={{ presence: !internalView }}
                            />
                          )}
                        </Grid>

                        {doo(() => {
                          if (returnReasonField.getValue() === RmaReturnReason.NOT_NEEDED) {
                            return null
                          }
                          const items = filterValidatableItems(itemsField.getValue())
                          if (
                            Object.values(items).some(item => {
                              const requirement = item.product?.id
                                ? productRequirements[item.product.id]
                                : undefined
                              return requirement?.techCaseRequired && !item.tech_case_number
                            })
                          ) {
                            return (
                              <H3 color={brandColors.red}>
                                Your order includes items that require a Fortus technical case
                                number.
                              </H3>
                            )
                          }
                          return null
                        })}

                        <FloatingBox
                          css={
                            itemsField.getError()?.all
                              ? css`
                                  ${FloatingBox.vars.borderColor}: red;
                                `
                              : undefined
                          }
                          footer={
                            <Grid columns={["max-content", "max-content"]} gap={8}>
                              <Button
                                leftIcon={icons.plus}
                                onClick={() => {
                                  const nextIdx = genId()
                                  itemsField.setValue({
                                    ...itemsField.getValue(),
                                    [nextIdx.toString()]: newBaseItem(),
                                  })
                                }}
                              >
                                Item
                              </Button>
                              <Button
                                leftIcon={icons.plus}
                                onClick={() => {
                                  const nextIdx = genId()
                                  itemsField.setValue({
                                    ...itemsField.getValue(),
                                    [nextIdx.toString()]: newBaseItem({ comment: true }),
                                  })
                                }}
                              >
                                Comment
                              </Button>
                            </Grid>
                          }
                        >
                          <Grid gap={8}>
                            {Object.keys(filterDeletedItems(itemsField.getValue()))?.map(refId => (
                              <ItemField
                                key={refId}
                                refId={refId}
                                isReceivingBranch={isReceivingBranch}
                                customerView={customerView}
                              />
                            ))}
                          </Grid>
                          {itemsField.getError()?.all && (
                            <React.Fragment>
                              <Spacer />
                              <P color="error">{itemsField.getError()?.all}</P>
                            </React.Fragment>
                          )}
                          {doo(() => {
                            const items = filterDeletedItems(itemsField.getValue())
                            const returnMessages = Object.values(items)
                              .map(item => item.product?.product_group?.rma_return_message)
                              .filter(Boolean)
                              .filter((a, b, c) => c.indexOf(a) === b) as string[]
                            if (returnMessages.length > 0) {
                              return (
                                <InfoBanner
                                  heading="Important Return Information"
                                  message={
                                    <div>
                                      {returnMessages.map(x => (
                                        <P key={x}>{x}</P>
                                      ))}
                                    </div>
                                  }
                                />
                              )
                            }
                            return null
                          })}
                        </FloatingBox>

                        <FloatingBox>
                          <Grid gap={8}>
                            <Form2.Text field="creditNumber" />
                            <Form2.TextArea
                              field="additionalDetails"
                              rows="auto"
                              placeholder="Enter any more information..."
                            />
                            <Form2.Text
                              field="returnsNumber"
                              label="Supplier Returns Number"
                              placeholder="RMA12345"
                              validations={{ presence: returnNumberRequired }}
                            />
                          </Grid>
                        </FloatingBox>

                        <FloatingBox header="Attachments">
                          <AttachmentsField
                            onAddFile={file => {
                              attachmentsField.setValue([
                                ...(attachmentsField.getValue() ?? []),
                                { id: fakeID(), ...file },
                              ])
                            }}
                            onRemove={fileIds => {
                              attachmentsField.setValue(
                                (attachmentsField.getValue() ?? []).filter(
                                  (file: { fileboyId: string }) =>
                                    !fileIds.includes(file.fileboyId),
                                ),
                              )
                            }}
                            value={attachmentsField.getValue() ?? []}
                          />
                        </FloatingBox>
                      </Grid>
                    </div>

                    {!customerView && (
                      <div
                        css={css`
                          background-color: #f5f5f5;
                          padding: 12px;
                        `}
                      >
                        <Grid gap={8}>
                          {customerField.getValue() && (
                            <HoverCustomerLoader id={customerField.getValue()?.id} inline />
                          )}
                          {orderField.getValue() && (
                            <HoverOrderLoader id={orderField.getValue()!.id} inline />
                          )}
                          {Object.values(filterDeletedItems(itemsField.getValue()))
                            .filter(x => x?.product)
                            .map(p =>
                              p ? (
                                <HoverProductLoader key={p.id} id={p.product!.id} inline />
                              ) : null,
                            )}
                        </Grid>
                      </div>
                    )}
                  </Grid>

                  {saveErrors.getError() && saveErrors.getError().length > 0 && (
                    <React.Fragment>
                      <Spacer />
                      <div
                        css={css`
                          border: 2px solid ${themeColors.alertRed};
                          padding: 16px;
                          border-radius: 3px;
                        `}
                      >
                        {saveErrors.getError().map((error: string) => (
                          <P key={error} color="red">
                            {error}
                          </P>
                        ))}
                      </div>
                    </React.Fragment>
                  )}
                  <Spacer />
                </div>
              )
            }}
          />
          <div
            css={css`
              padding: 0 12px 12px;
            `}
          >
            <Form2.WithForm
              render={form => (
                <BaseButton {...buttonConfigs.formSaveButton} disabled={form.state.isSubmitting} />
              )}
              subscribe={{ dataChange: true, submitting: true }}
            />
          </div>
        </ValidateRmaForm>
      </Form2.Form>
    </React.Fragment>
  )
}

function ValidateCustomerPresence() {
  Form2.useFormSubmitLock(state => {
    const customerField = state.getField("customer")
    const value = customerField.getValue()
    if (!value) {
      customerField.setError("must be present")
      return false
    }
  })
  return null
}

type ISerialNumber = IRecord<"serial_numbers", "serial_number"> & { product: IRmaProduct }

function ItemField({
  refId,
  isReceivingBranch,
  customerView,
}: {
  refId: string
  isReceivingBranch?: boolean
  customerView?: boolean
}) {
  const isTablet = useMediaQuery({ maxWidth: 1200 })
  const branch = Form2.useField<IRmaBranch | undefined>("branch", { subscribe: true })
  const orderField = Form2.useField<IRmaOrder | undefined>("order", { subscribe: true })
  const itemsField = Form2.useField<Record<string, IRmaItem>>("items", {
    subscribe: true,
    defaultValue: {},
  })
  const customerField = Form2.useField<IRmaCustomer | undefined>("customer", { subscribe: true })
  const returnReasonField = Form2.useField<RmaReturnReason | undefined>("returnReason", {
    subscribe: true,
  })
  const productRequirement = useRmaProductRequirement(itemsField.getValue()?.[refId]?.product?.id)

  const [serialNumbers, setSerialNumbers] = useState<ISerialNumber[] | null>()

  // for regular select when a product is selected ~ load serial numbers & set if only has 1.
  async function loadSerialNumbers(product?: { id: string }) {
    if (!product) {
      setSerialNumbers(null)
      return
    }
    let serialNumbers = await skeem.fetch2<ISerialNumber[]>({
      model: {
        name: "serial_numbers",
        attributes: ["serial_number", { name: "product", attributes: rmaProductAttributes }],
        filter: { matches: { product: product.id } },
        paginate: { size: 30 },
      },
    })
    // filter only to this branch and remove any dupes.
    serialNumbers = serialNumbers
      .filter(x => x.product.branch?.id === branch.getValue()?.id)
      .filter(
        (item, index, arr) => arr.findIndex(v => v.serial_number === item.serial_number) === index,
      )
    if (serialNumbers.length === 1) {
      itemsField.setValue({
        ...itemsField.getValue(),
        [refId]: {
          ...itemsField.getValue()[refId],
          serial_number: serialNumbers[0].serial_number,
        },
      })
    }
    setSerialNumbers(serialNumbers)
  }

  // for async select when a product is not selected
  async function lookupSerialNumber(serial: string) {
    if (!serial || serial.length < 3) {
      setSerialNumbers([])
      return []
    }
    const order = orderField.getValue() || itemsField.getValue()[refId]?.order
    const productValue = itemsField.getValue()[refId]?.product
    const queryFilter = {
      matches: { serial_number: { like: serial } },
    }
    const andFilter = [queryFilter] as any[]

    andFilter.push({
      gte: [
        {
          count: {
            path: [
              {
                name: "product",
                filter: order
                  ? {
                      gte: [
                        {
                          count: {
                            path: [
                              "order_items",
                              { name: "order", filter: { matches: { id: order.id } } },
                            ],
                          },
                        },
                        { value: 1 },
                      ],
                    }
                  : undefined,
              },
            ],
          },
        },
        { value: 1 },
      ],
    })
    if (productValue) {
      andFilter.push({ matches: { product: productValue.id } })
    }
    const numbers = await skeem.fetch2<ISerialNumber[]>({
      model: {
        name: "serial_numbers",
        attributes: ["serial_number", { name: "product", attributes: rmaProductAttributes }],
        filter: { and: andFilter },
        paginate: { size: 30 },
      },
    })
    const filtered = numbers.filter(n => n.product?.branch?.id === branch.getValue()?.id)
    setSerialNumbers(filtered)
    return filtered
  }

  function handleChangeProduct(product?: any) {
    loadSerialNumbers(product)
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        product,
      },
    })
    checkIfSerialNumberIsRequired()
  }

  function handleChangeQuantity(qty: number | null) {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        quantity: qty,
      },
    })
  }

  function handleChangeTechCaseNumber(nmbr: string) {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        tech_case_number: nmbr,
      },
    })
    checkIfTechCaseNumberRequired()
  }

  function checkIfTechCaseNumberRequired() {
    const currentFieldValue = itemsField.getValue()?.[refId]
    const techCaseNumber = currentFieldValue?.tech_case_number

    if (techCaseNumber?.trim() || returnReasonField.getValue() === RmaReturnReason.NOT_NEEDED) {
      setError({ tech_case_number: undefined })
      return
    }

    if (productRequirement?.techCaseRequired) {
      setError({ tech_case_number: "Tech case number is required for this product" })
      return
    }
    setError({ tech_case_number: undefined })
  }

  const handleChangeSerialNumber = useAsyncCallback(async (serialNumber: { value: string }) => {
    const itemFieldValue = itemsField.getValue()
    const currentFieldValue = itemFieldValue[refId]
    itemsField.setValue({
      ...itemFieldValue,
      [refId]: {
        ...currentFieldValue,
        serial_number: serialNumber?.value ?? "",
      },
    })
    checkIfSerialNumberIsRequired()
    if (!serialNumber?.value) {
      return
    }

    // Attempt to find the order and product details based on the serial number
    const customerId = customerField.getValue()?.id

    type SerialNumber = IRecord<"serial_numbers"> & {
      product: IRmaProduct | null
      order_item: IRecord<"order_items"> & { order: IRmaItemOrder }
    }
    const records = await skeem.fetch2<SerialNumber[]>({
      model: {
        name: "serial_numbers",
        attributes: [
          { name: "product", attributes: rmaProductAttributes },
          {
            name: "order_item",
            attributes: [{ name: "order", attributes: rmaItemOrderAttributes }],
          },
        ],
        filter: {
          and: [
            { matches: { serial_number: serialNumber.value } },
            customerId
              ? { eq: [{ path: ["order_item", "order", "customer", "id"] }, { value: customerId }] }
              : undefined,
          ].filter(Boolean),
        },
      },
    })
    if (records.length === 1) {
      const record = records[0]
      if (record.product)
        itemsField.setValue({
          ...itemsField.getValue(),
          [refId]: {
            ...itemsField.getValue()[refId],
            product: record.product,
            order: record.order_item.order,
          },
        })
    }
    console.log("SN", records)
  })

  function handleRemoveItem() {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: { ...itemsField.getValue()[refId], _delete: true },
    })
  }

  async function checkIfSerialNumberIsRequired() {
    const itemFieldValue = itemsField.getValue()
    const currentFieldValue = itemFieldValue[refId]

    const product = currentFieldValue.product
    const serialNumber = currentFieldValue.serial_number

    if (serialNumber || !product) {
      // if we have a serial number or no product, we can assume it's valid
      setError({ serial_number: undefined })
      return
    }

    const productRecord = await window.skeem.fetch2<
      IRecord<"products"> & {
        product_group?: IRecord<"product_groups"> & {
          product_group_master?: IRecord<"product_group_masters", "rma_mandatory_serial_number">
        }
      }
    >({
      first: {
        name: "products",
        matches: { id: product.id },
        attributes: [
          {
            name: "product_group",
            attributes: [
              { name: "product_group_master", attributes: ["rma_mandatory_serial_number"] },
            ],
          },
        ],
      },
    })
    if (
      productRecord?.product_group?.product_group_master?.rma_mandatory_serial_number &&
      !serialNumber
    ) {
      setError({ serial_number: "Serial number is required for this product" })
      return
    }
    setError({ serial_number: undefined })
    return
  }

  function handleChangeOrder(order?: IRmaItemOrder) {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        order,
      },
    })
  }

  const value = itemsField.getValue()?.[refId]
  const error = itemsField.getError()?.[refId] as Record<string, string> | undefined

  useAsyncEffect(async () => {
    if (value.product) {
      await loadSerialNumbers(value.product)
    }
  }, [branch.getValue()?.id, orderField.getValue()?.id, value?.product?.id])

  const orderItems = orderField
    .getValue()
    ?.items?.filter(item => item.product?.id === value.product?.id)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  function debouncedSearchSerialNumber(serial: string) {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(async () => {
      await lookupSerialNumber(serial ?? "")
    }, 150)
    return serial
  }

  function setError(errorData?: Record<string, any>) {
    const value = itemsField.getError() ?? {}
    itemsField.setError({
      ...value,
      [refId]: { ...value[refId], ...errorData },
    })
  }

  const hasOrder = !!orderField.getValue()?.id

  function handleTextLineChange(comment: string) {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        comment: comment,
      },
    })
  }

  if (value.item_type == "comment") {
    return (
      <FloatingBox color={themeColors.lightestGrey}>
        <Grid columns={[{ fr: 1 }, 36]} alignItems={value.product ? "start" : "end"} gap={8}>
          <Fields.TextArea onChange={handleTextLineChange} value={value.comment} />
          <IconButton
            onClick={handleRemoveItem}
            icon={icons.x}
            buttonColor="red"
            buttonStyle="outline"
            square
          />
        </Grid>
      </FloatingBox>
    )
  }

  const cols = isTablet
    ? undefined
    : [...(hasOrder ? [{ fr: 1 }, 120] : [{ fr: 1 }, 120, { fr: 1 }]), 36]

  return (
    <FloatingBox color={themeColors.lightestGrey}>
      <Grid gap={16}>
        <Grid columns={[{ fr: 1 }, 36]} alignItems={value.product ? "start" : "end"} gap={8}>
          <RmaProductSelect
            onChange={handleChangeProduct}
            value={value.product}
            error={error?.product}
            includeLink={!customerView}
            order={value.order}
          />
          <IconButton
            onClick={handleRemoveItem}
            icon={icons.x}
            buttonColor="red"
            buttonStyle="outline"
            square
          />
        </Grid>
        <Grid columns={cols} gap={8}>
          <div>
            <Fields.CreatableSelect
              label="Serial Number"
              onChange={opt => handleChangeSerialNumber(opt)}
              options={[value?.serial_number, ...(serialNumbers?.map(x => x.serial_number) ?? [])]
                .filter(Boolean)
                .map(x => ({ label: x, value: x }))}
              value={
                value?.serial_number
                  ? { label: value.serial_number, value: value.serial_number }
                  : undefined
              }
              error={error?.serial_number}
              onCreateOption={opt => handleChangeSerialNumber({ value: opt })}
              onInputChange={
                value.product ? undefined : value => debouncedSearchSerialNumber(value)
              }
              getNewOptionData={opt => ({ label: opt, value: opt })}
              createOptionPosition="first"
              placeholder="Select or enter new..."
              isClearable
            />
            {!!value.serial_number && (
              <div
                css={css`
                  margin-top: 4px;
                `}
              >
                <CopyButton value={value.serial_number} />
              </div>
            )}

            {productRequirement?.techCaseRequired &&
              returnReasonField.getValue() !== RmaReturnReason.NOT_NEEDED && (
                <div>
                  <Spacer />
                  <Fields.Text
                    label="Tech Case Number"
                    onChange={handleChangeTechCaseNumber}
                    value={value.tech_case_number}
                    error={error?.tech_case_number}
                    showRequiredStar
                  />
                </div>
              )}
          </div>
          <Fields.Number2
            label="Quantity"
            onChange={handleChangeQuantity}
            value={value.quantity}
            error={error?.quantity}
            showRequiredStar
          />
          {!hasOrder && (
            <SimpleOrderField
              key={value.id}
              onChange={handleChangeOrder}
              value={value.order}
              error={error?.order}
              product={value.product}
              customerView={customerView}
            />
          )}
        </Grid>
        {isReceivingBranch && orderItems && orderItems?.length > 0 && (
          <div>
            <P>{orderItems?.length} matching order items</P>
            {orderItems?.map(item => (
              <div key={item.id}>
                Cost price:{" "}
                {formatPrice(item.cost_price_pennies ?? 0, { isPennies: true, currency: "" })};
                Price: {formatPrice(item.price_pennies ?? 0, { isPennies: true, currency: "" })}
              </div>
            ))}
          </div>
        )}
      </Grid>
    </FloatingBox>
  )
}

type ISelectProduct = IRmaProduct & {
  fileboyImageId?: string
  product_group?: IRecord<"product_groups", "rma_required_tech_case_number">
}
function formatProductOption(option: ISelectProduct, isBranchArea?: boolean) {
  return (
    <Grid columns={[40, "auto"]} gap={16}>
      <div>
        <ProductMasterThumbnail productId={option?.id} />
      </div>
      <div>
        <P blockSpacing={0}>{option.stock_code}</P>
        <P blockSpacing={0} size="small">
          {option.body}
        </P>
        {isBranchArea && (
          <Link
            onClick={event => event.stopPropagation()}
            target="_blank"
            to={buildRoute(eRoutes["/stock/products/:id"], { id: option.id })}
          >
            View Product
          </Link>
        )}
      </div>
    </Grid>
  )
}

export function CopyButton({ value }: { value: string }) {
  const copy = useClipboard()
  const alerts = useSiteAlerts()
  async function handleCopy() {
    try {
      await copy(value)
      alerts.genericSuccess(`${value} copied to clipboard`)
    } catch (e) {
      alerts.genericError("Failed to copy to clipboard")
      console.error(e)
    }
  }
  return (
    <Button
      buttonStyle="text"
      extendTheme={{ padding: { sides: 2, topAndBottom: 2, innerSides: 8 }, weight: 400 }}
      color={lightenDarkenColor(brandColors.darkMint, -0.2)}
      leftIcon={icons.copy}
      onClick={handleCopy}
    >
      Copy {value}
    </Button>
  )
}

export function RmaProductSelect({
  onChange,
  value,
  error,
  includeLink,
  order,
}: {
  onChange: (data?: { id: string }) => void
  value?: { id: string }
  error?: string
  includeLink?: boolean
  order?: { id: string }
}) {
  const branchField = Form2.useField<IRecord<"branches"> | undefined>("branch", { subscribe: true })
  const orderField = Form2.useField<{ id: string } | undefined>("order", { subscribe: true })
  const customerField = Form2.useField<{ id: string } | undefined>("customer", { subscribe: true })
  const productId = value?.id

  const [selectedProduct, setSelectedProduct] = useState<ISelectProduct | null>(null)

  async function handleChange(data?: { id: string }) {
    if (data?.id && selectedProduct?.id !== data.id) {
      const product = await loadProduct(data.id)
      onChange(product)
    } else {
      onChange(undefined)
      setSelectedProduct(null)
    }
  }

  useAsyncEffect(async () => {
    if (productId) {
      await loadProduct(productId)
    }
  }, [productId])

  const skeem = window.skeem

  const productSelectAttributes: any[] = [
    ...rmaProductAttributes,
    { name: "product_group", attributes: ["rma_required_tech_case_number"] },
    {
      as: "fileboyImageId",
      value: {
        first: {
          path: [
            "product_master",
            { name: "files", filter: { matches: { fileType: fileTypes.image } } },
            "fileboyId",
          ],
        },
      },
    },
  ]

  async function loadRecords(query: string) {
    const pageSize = 15
    const productFilters: any[] = []
    if (branchField.getValue()) {
      productFilters.push({ matches: { branch: branchField.getValue()?.id } })
    }
    const orderValue = orderField.getValue() || order
    if (orderValue) {
      productFilters.push({
        gt: [
          {
            count: {
              path: [
                "order_items",
                {
                  name: "order",
                  filter: { matches: { id: orderValue?.id } },
                },
              ],
            },
          },
          { value: 0 },
        ],
      })
    }
    // Serial numbers are deliberately not an OR arm here: a correlated count over serial_numbers
    // stops Postgres using the trigram indexes and re-runs the serial search once per product
    // (8.9s+ on prod vs 5ms without it). They are searched separately below if there's room.
    const products = await skeem.fetch2<ISelectProduct[]>({
      model: {
        name: "products",
        attributes: productSelectAttributes,
        filter: {
          and: [
            {
              or: [
                { matches: { stock_code: { like: query } } },
                { matches: { body: { like: query } } },
              ],
            },
            ...productFilters,
          ],
        },
        paginate: { size: pageSize },
      },
    })
    if (products.length >= pageSize || query.length < 3) {
      return products
    }

    const serials = await skeem.fetch2<{ product?: ISelectProduct }[]>({
      model: {
        name: "serial_numbers",
        attributes: [{ name: "product", attributes: productSelectAttributes }],
        filter: {
          and: [
            { matches: { serial_number: { like: query } } },
            ...productFilters.map(f => ({
              gte: [{ count: { path: [{ name: "product", filter: f }] } }, { value: 1 }],
            })),
          ],
        },
        paginate: { size: pageSize * 2 },
      },
    })
    const seen = new Set(products.map(p => p.id))
    for (const serial of serials) {
      if (products.length >= pageSize) break
      if (serial.product && !seen.has(serial.product.id)) {
        seen.add(serial.product.id)
        products.push(serial.product)
      }
    }
    return products
  }

  async function loadProduct(id: string) {
    const product = await skeem.fetch2<ISelectProduct>({
      first: {
        name: "products",
        attributes: [
          ...rmaProductAttributes,
          {
            as: "fileboyImageId",
            value: {
              first: {
                path: [
                  "product_master",
                  { name: "files", filter: { matches: { fileType: fileTypes.image } } },
                  "fileboyId",
                ],
              },
            },
          },
        ],
        matches: { id },
      },
    })
    setSelectedProduct(product)
    return product
  }

  const selectListKey = [
    order?.id,
    orderField.getValue()?.id,
    customerField.getValue()?.id,
    branchField.getValue()?.id,
  ].join("-")

  if (selectedProduct) {
    return (
      <div
        css={css`
          padding: 8px;
          border: 1px solid ${colors.lightBorder};
          border-radius: 3px;
          background-color: white;
        `}
      >
        <Grid columns={["auto", "max-content"]} gap={8}>
          <H4>Product</H4>
          <a
            css={css`
              cursor: pointer;
            `}
            onClick={() => {
              setSelectedProduct(null)
              onChange(undefined)
            }}
          >
            clear
          </a>
        </Grid>
        <Grid columns={[40, "auto"]} gap={16} alignItems="center">
          <ProductMasterThumbnail productId={selectedProduct.id} />
          <div>
            <P>{selectedProduct.stock_code ?? "-"}</P>
            {selectedProduct.body && <P>{selectedProduct.body}</P>}
            {includeLink && (
              <Link
                target="_blank"
                to={buildRoute(eRoutes["/stock/products/:id"], {
                  id: selectedProduct.id,
                })}
              >
                View Product
              </Link>
            )}
          </div>
        </Grid>
      </div>
    )
  }

  return (
    <Fields.AsyncSelect
      key={selectListKey}
      label="Product"
      loadOptions={loadRecords}
      onChange={handleChange}
      value={selectedProduct}
      getOptionLabel={opt => opt.stock_code}
      getOptionValue={opt => opt.id}
      formatOptionLabel={opt => formatProductOption(opt, includeLink)}
      error={error}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length === 0 ? "Type to search..." : "No results"
      }
      showRequiredStar
      isClearable
    />
  )
}

async function checkItemValidForRma({
  returnReason,
  orderDate,
  items,
  createdAt,
}: {
  returnReason: RmaReturnReason
  orderDate: Date
  items: Record<string, IRmaItem>
  createdAt: Date
}) {
  if (returnReason === RmaReturnReason.NOT_NEEDED) {
    // 30 day return policy
    if (!orderDate || differenceInDays(createdAt, orderDate) > 30) {
      return {
        all: "Outside of 30 days - Please be aware your return does not meet our usual returns policy, please contact your local branch for further assistance",
      }
    }
  }

  const errorMap: Record<string, Record<string, string>> = {}
  if (returnReason === RmaReturnReason.FAULTY) {
    // warranty check
    const productIds = Object.values(items)
      .map(x => x.product?.id)
      .filter(Boolean)
    const records = await skeem.fetch2<
      (IRecord<"products"> & { product_master?: IRecord<"product_masters", "warranty_duration"> })[]
    >({
      model: {
        name: "products",
        attributes: [{ name: "product_master", attributes: ["warranty_duration"] }],
        matches: { id: { or: productIds } },
      },
    })
    const itemData = Object.entries(items)
    for (const productData of records) {
      const itemKey = itemData.find(x => x[1].product?.id === productData.id)?.[0] ?? ""
      const warrantyDurationMonths = productData.product_master?.warranty_duration ?? 12 * 3
      const message =
        "Not in Warranty - This item does not fall within our expected warranty periods, please contact your local branch for further assistance"

      if (!orderDate) {
        errorMap[itemKey] = { product: message }
        continue
      }

      if (warrantyDurationMonths) {
        const warrantyEnd = add(orderDate, { months: warrantyDurationMonths })

        if (warrantyEnd.valueOf() < createdAt.valueOf()) {
          errorMap[itemKey] = { product: message }
        }
      }
    }
  }
  const hasErrors = Object.keys(errorMap).length > 0
  console.log("Warranty", errorMap)
  return hasErrors ? errorMap : null
}

export function ValidateRmaForm({
  setReturnNumberRequired,
  createdAt,
  children,
}: {
  setReturnNumberRequired: React.Dispatch<React.SetStateAction<boolean>>
  createdAt?: Date
  children?: React.ReactNode
}) {
  const createdAtDateRef = useRef(createdAt ? createdAt : new Date())
  createdAtDateRef.current = createdAt ? createdAt : new Date()
  const [productRequirements, setProductRequirements] = useState<
    Record<string, IRmaProductRequirement>
  >({})

  // =========================== FIELD DEFINITIONS =================================================
  const returnReasonField = Form2.useField<RmaReturnReason | undefined>("returnReason", {
    subscribe: true,
  })
  const orderDateField = Form2.useField<Date | undefined>("orderDate", { subscribe: true })
  const itemsField = Form2.useField<Record<string, IRmaItem>>("items", {
    subscribe: true,
  })
  const orderField = Form2.useField<{ id: string } | undefined>("order", { subscribe: true })
  const branchField = Form2.useField<IRecord<"branches", "name"> | undefined>("branch", {
    subscribe: true,
  })
  const customerField = Form2.useField<{ id: string } | undefined>("customer", {
    subscribe: true,
  })
  const statusField = Form2.useField<{ value: RmaStatus; label: string }>("status", {
    subscribe: true,
  })
  const assignedToField = Form2.useField<IRecord<"skeem_users"> | undefined>("assignedTo", {
    subscribe: true,
  })

  async function revalidateItems(): Promise<boolean> {
    const itemErrors: Record<string, Record<string, string> | string> = {}
    let hasErrors = false
    const items = filterValidatableItems(itemsField.getValue())
    const returnReason = returnReasonField.getValue()
    const orderDate = orderDateField.getValue()

    if (!Object.keys(items).length) {
      itemErrors.all = "At least 1 item is required"
      hasErrors = true
    }

    Object.entries(items).forEach(([key, item]) => {
      if (!item.product || typeof item.quantity !== "number" || item.quantity < 1) {
        const refErrors: Record<string, string> =
          typeof itemErrors[key] === "object"
            ? { ...(itemErrors[key] as Record<string, string>) }
            : {}
        if (!item.product) {
          refErrors.product = "is required"
        }
        if (typeof item.quantity !== "number" || item.quantity < 1) {
          refErrors.quantity = "is required"
        }
        itemErrors[key] = refErrors
        hasErrors = true
      }
    })

    if (returnReason && orderDate && Object.keys(items).length > 0) {
      const warrantyErrors = await checkItemValidForRma({
        returnReason,
        orderDate,
        items,
        createdAt: createdAtDateRef.current,
      })
      if (warrantyErrors) {
        for (const [key, val] of Object.entries(warrantyErrors)) {
          if (typeof val === "string") {
            itemErrors[key] = val
          } else {
            itemErrors[key] = {
              ...(typeof itemErrors[key] === "object"
                ? (itemErrors[key] as Record<string, string>)
                : {}),
              ...val,
            }
          }
        }
        hasErrors = true
      }
    }

    const productIds = Object.values(items)
      .map(x => x.product?.id)
      .filter(Boolean) as string[]
    const requirementsMap: Record<string, IRmaProductRequirement> = {}

    if (productIds.length > 0) {
      const products = await skeem.fetch2<
        (IRecord<"products"> & {
          product_group?: IRecord<"product_groups", "rma_required_tech_case_number"> & {
            product_group_master?: IRecord<"product_group_masters", "rma_mandatory_serial_number">
          }
        })[]
      >({
        model: {
          name: "products",
          matches: { id: { or: productIds } },
          attributes: [
            {
              name: "product_group",
              attributes: [
                "rma_required_tech_case_number",
                { name: "product_group_master", attributes: ["rma_mandatory_serial_number"] },
              ],
            },
          ],
        },
      })

      for (const product of products) {
        requirementsMap[product.id] = {
          serialRequired:
            !!product.product_group?.product_group_master?.rma_mandatory_serial_number,
          techCaseRequired: !!product.product_group?.rma_required_tech_case_number,
        }
      }
      setProductRequirements(requirementsMap)

      const skipTechCase = returnReason === RmaReturnReason.NOT_NEEDED
      for (const [refId, item] of Object.entries(items)) {
        if (!item.product?.id) {
          continue
        }
        const req = requirementsMap[item.product.id]
        if (!req) {
          continue
        }
        const refErrors: Record<string, string> =
          typeof itemErrors[refId] === "object"
            ? { ...(itemErrors[refId] as Record<string, string>) }
            : {}
        let refHasError = false
        if (req.serialRequired && !item.serial_number) {
          refErrors.serial_number = "Serial number is required for this product"
          refHasError = true
        }
        if (!skipTechCase && req.techCaseRequired && !item.tech_case_number) {
          refErrors.tech_case_number = "Tech case number is required for this product"
          refHasError = true
        }
        if (refHasError) {
          itemErrors[refId] = refErrors
          hasErrors = true
        }
      }
    } else {
      setProductRequirements({})
    }

    itemsField.setError(Object.keys(itemErrors).length > 0 ? itemErrors : undefined)
    return hasErrors
  }

  // ==================== PRODUCT GROUP RETURN NUM REQUIRED CHECK===================================
  async function handleCheckReturnsPolicy() {
    if (returnReasonField.getValue() == RmaReturnReason.NOT_NEEDED) {
      setReturnNumberRequired(false)
      return
    }
    const items = filterValidatableItems(itemsField.getValue())
    if (!items || Object.values(items).length === 0) {
      setReturnNumberRequired(false)
      return
    }
    const productIds = Object.values(items)
      .map(x => x.product?.id)
      .filter(Boolean)
    if (productIds.length === 0) {
      setReturnNumberRequired(false)
      return
    }
    type IRecordData = IRecord<"products"> & {
      product_group?: IRecord<"product_groups"> & {
        product_group_master?: IRecord<"product_group_masters", "require_returns_number">
      }
    }
    const records = await skeem.fetch2<IRecordData[]>({
      model: {
        name: "products",
        attributes: [
          {
            name: "product_group",
            attributes: [{ name: "product_group_master", attributes: ["require_returns_number"] }],
          },
        ],
        matches: { id: { or: productIds } },
      },
    })
    setReturnNumberRequired(
      records.filter(r => r.product_group?.product_group_master?.require_returns_number).length > 0,
    )
  }

  // Tracking changes to state and rechecking validity
  // using a debounced state as this is getting trigged extremely frequently
  const [watchState, setWatchState] = useState<Record<string, any>>({})
  useEffect(() => {
    setDebouncedState(JSON.stringify(watchState))
  }, [JSON.stringify(watchState)])
  const [debouncedState, setDebouncedState] = useDebouncedState(JSON.stringify(watchState))

  useAsyncEffect(async () => {
    await revalidateItems()
    await handleCheckReturnsPolicy()
  }, [debouncedState])

  useEffect(() => {
    setWatchState({
      rrf: returnReasonField.getValue(),
      odf: orderDateField.getValue(),
      pf: JSON.stringify(
        Object.values(filterValidatableItems(itemsField.getValue())).map(x => x.product?.id),
      ),
      of: orderField.getValue(),
    })
  }, [])

  useEffect(() => {
    const cb1 = returnReasonField.events.on(Form2.fieldEvents.change, () => {
      setWatchState(old => ({ ...old, rrf: returnReasonField.getValue() }))
    })
    const cb2 = orderDateField.events.on(Form2.fieldEvents.change, () => {
      setWatchState(old => ({ ...old, odf: orderDateField.getValue() }))
    })
    const cb3 = itemsField.events.on(Form2.fieldEvents.change, () => {
      setWatchState(old => ({
        ...old,
        pf: JSON.stringify(
          Object.values(filterValidatableItems(itemsField.getValue())).map(x => x.product?.id),
        ),
      }))
    })
    const cb4 = orderField.events.on(Form2.fieldEvents.change, () => {
      setWatchState(old => ({ ...old, of: orderField.getValue() }))
    })

    return () => {
      cb1()
      cb2()
      cb3()
      cb4()
    }
  }, [])

  // ======================== VALIDATE FIELD DATA PRESENCE==========================================
  Form2.useFormSubmitLock(async () => {
    let hasErrors = false
    if (!branchField.getValue()) {
      branchField.setError("Branch is required")
      hasErrors = true
    } else {
      branchField.setError(undefined)
    }

    if (await revalidateItems()) {
      hasErrors = true
    }

    if (orderField.getValue()?.id) {
      // if order field is set, clear order on all items
      itemsField.setValue(
        Object.fromEntries(
          Object.entries(itemsField.getValue()).map(([key, item]) => [
            key,
            { ...item, order: undefined },
          ]),
        ),
      )
    }

    if (statusField.getValue()?.value !== RmaStatus.PENDING && !assignedToField.getValue()) {
      assignedToField.setError("Assignee is required when not pending")
      hasErrors = true
    } else {
      assignedToField.setError(undefined)
    }

    if (!customerField.getValue()) {
      customerField.setError("Customer is required")
      hasErrors = true
    } else {
      customerField.setError(undefined)
    }

    if (hasErrors) {
      return false
    }
  })

  return (
    <RmaProductRequirementsContext.Provider value={productRequirements}>
      {children}
    </RmaProductRequirementsContext.Provider>
  )
}

function removeLeadingZeros(str: string) {
  const leadingZeros = /(^0*).*$/i.exec(str)
  if (leadingZeros) {
    return str.replace(leadingZeros[1], "")
  }
  return str
}

export function OrderLookupField({ customerView }: { customerView?: boolean }) {
  const [selectedOption, setSelectedOption] = React.useState<IRmaItemOrder | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const branchField = Form2.useField<IRecord<"branches"> | undefined>("branch", { subscribe: true })
  const orderDateField = Form2.useField<Date | undefined>("orderDate", { subscribe: true })
  const customerField = Form2.useField<IRecord<"customers"> | undefined>("customer", {
    subscribe: true,
  })
  const orderField = Form2.useField<IRecord<"orders", "identifier" | "customer"> | undefined>(
    "order",
    { subscribe: true },
  )
  const salesNumberField = Form2.useField<string | undefined>("salesNumber", { subscribe: true })
  const [inputValue, setInputValue] = React.useState(
    orderField.getValue()?.identifier || salesNumberField.getValue() || "",
  )

  const isMismatchedCustomer =
    selectedOption && selectedOption?.customer?.id !== customerField.getValue()?.id

  useEffect(() => {
    // debounce
    const timeout = setTimeout(async () => {
      await handleLookupOptions(inputValue)
    }, 250)

    return () => {
      clearTimeout(timeout)
    }
  }, [inputValue, customerField.getValue()?.id])

  const [orderOptions, setOrderOptions] = React.useState<IRmaItemOrder[]>([])

  async function handleLookupOptions(query: string) {
    try {
      setIsLoading(true)

      const customer = customerField.getValue()

      const identifier = removeLeadingZeros(query)

      const orders = await skeem.fetch2<IRmaItemOrder[]>({
        model: {
          name: "orders",
          attributes: [
            "identifier",
            "purchase_order_reference",
            {
              as: "invoice_number",
              value: {
                first: {
                  path: [
                    {
                      name: "invoice_items",
                      filter: { matches: { invoice_document_number: { not: "" } } },
                    },
                    "invoice_document_number",
                  ],
                },
              },
            },
            { as: "invoice_date", value: { first: { path: ["invoice_items", "invoice_date"] } } },
            { name: "delivery", attributes: ["confirmed_at"] },
            { name: "customer" },
          ],
          filter: {
            and: [
              { matches: { branch: branchField.getValue()?.id, customer: customer?.id } },
              {
                or: [
                  // invoice number - orders have invoice items, these have invoice_document_number
                  {
                    gt: [
                      {
                        count: {
                          path: [
                            {
                              name: "invoice_items",
                              filter: {
                                matches: { invoice_document_number: { like: identifier } },
                              },
                            },
                          ],
                        },
                      },
                      { value: 0 },
                    ],
                  },
                  // Delivery Note number - this is order identifier
                  { matches: { identifier: { like: identifier } } },
                  // Customer Order Number - this is the purchase order ref
                  { matches: { purchase_order_reference: { like: identifier } } },
                ],
              } as any,
            ],
          },
        },
      })

      console.log("orders", orders)
      // if we got a single match, select it (e.g 0001234 -> 1234)
      setOrderOptions(orders)
    } finally {
      setIsLoading(false)
    }
  }

  useAsyncEffect(async () => {
    const orderId = orderField.getValue()?.id
    if (orderId) {
      if (selectedOption?.id === orderId) {
        return
      }
      const order = await skeem.fetch2<IRmaItemOrder>({
        first: {
          name: "orders",
          attributes: [
            "identifier",
            "purchase_order_reference",
            {
              as: "invoice_number",
              value: {
                first: {
                  path: [
                    {
                      name: "invoice_items",
                      filter: { matches: { invoice_document_number: { not: "" } } },
                    },
                    "invoice_document_number",
                  ],
                },
              },
            },
            { as: "invoice_date", value: { first: { path: ["invoice_items", "invoice_date"] } } },
            { name: "delivery", attributes: ["confirmed_at"] },
            { name: "customer" },
          ],
          filter: { matches: { id: orderId } },
        },
      })
      setSelectedOption(order)
    } else if (salesNumberField.getValue() && !customerView) {
      setSelectedOption({
        id: "",
        customer: null,
        delivery: null,
        identifier: salesNumberField.getValue() || "",
        invoice_number: "",
        purchase_order_reference: "",
      })
    }
  }, [])

  function handleChange(option: IRmaItemOrder | null) {
    // set the locally cached value regardless so it shows correctly
    // validations will indicate if it is valid or not
    setSelectedOption(option)

    if (option && option.id) {
      console.log("Assigned ORDER", option)
      // if the selected option has an id, then it's a real order so set both values
      orderField.setValue(option)
      salesNumberField.setValue(option.identifier)

      const date = option?.invoice_date || option?.delivery?.confirmed_at
      if (date) {
        orderDateField.setValue(new Date(date))
      }

      if (!customerField.getValue()) {
        // if the customer is not set, set it from the order
        customerField.setValue(option.customer ?? undefined)
      }
    } else if (option) {
      console.log("Assigned CUSTOM", option)
      // if selected option has no id, it's a fake option (the user entered a value), so set the sales number field
      // only and clear our order field
      orderField.setValue(undefined)
      salesNumberField.setValue(option.identifier)
    } else {
      console.log("Assigned NO", option)
      // no option was selected so clear both fields
      orderField.setValue(undefined)
      salesNumberField.setValue("")
    }
  }

  // only include the fake option for branch users, and do not include if there is an order with the same identifier
  // ignoring leading 0's or there is no input value
  const fakeOption: IRmaItemOrder | null =
    !customerView &&
    inputValue !== "" &&
    !orderOptions.find(x => removeLeadingZeros(x.identifier) === removeLeadingZeros(inputValue))
      ? {
          id: "",
          customer: null,
          delivery: null,
          identifier: inputValue,
          invoice_number: "",
          purchase_order_reference: "",
        }
      : null
  return (
    <Grid gap={2}>
      <Fields.Select2
        label={<Label optional>Order</Label>}
        onInputChange={setInputValue}
        inputValue={inputValue}
        error={orderField.getError()}
        options={[fakeOption, ...orderOptions].filter(Boolean)}
        onChange={handleChange}
        value={selectedOption}
        getOptionLabel={opt => opt.identifier}
        getOptionValue={opt => opt.id}
        isLoading={isLoading}
        formatOptionLabel={opt =>
          !opt.id
            ? opt.identifier
            : [
                { label: "Invoice Number", value: opt.invoice_number },
                { label: "Delivery Note Number", value: opt.identifier },
                { label: "Customer Order Number", value: opt.purchase_order_reference },
              ]
                .filter(x => !!x.value)
                .map(x => `${x.label}: ${x.value}`)
                .join("; ")
        }
        // overide the select search as we are already filtering our options down
        filterOption={() => true}
        isClearable
      />
      {isMismatchedCustomer && !customerView && (
        <div>
          <P color="red">This order does not belong to the selected customer.</P>
        </div>
      )}
    </Grid>
  )
}

export function SimpleOrderField({
  onChange,
  value,
  error,
  customerView,
  product,
}: {
  onChange: (data?: IRmaItemOrder) => void
  value?: IRmaItemOrder
  error?: string
  customerView?: boolean
  product?: { id: string }
}) {
  function formatLabel(opt: IRmaItemOrder) {
    return [
      { label: "Invoice Number", value: opt.invoice_number },
      { label: "Delivery Note Number", value: opt.identifier },
      { label: "Customer Order Number", value: opt.purchase_order_reference },
    ]
      .filter(x => !!x.value)
      .map(x => `${x.label}: ${x.value}`)
      .join("; ")
  }
  const [isLoading, setIsLoading] = React.useState(false)
  const hasMountedRef = React.useRef(false)
  const branchField = Form2.useField<IRecord<"branches"> | undefined>("branch", { subscribe: true })
  const customerField = Form2.useField<IRecord<"customers"> | undefined>("customer", {
    subscribe: true,
  })
  const [inputValue, setInputValue] = React.useState("")
  const [showMismatchedCustomerError, setShowMismatchedCustomerError] = React.useState(false)

  function checkOrderCustomer() {
    // check order belongs to customer
    console.log("CHECKORDERCUSTOMER", value, customerField.getValue())
    if (
      value?.customer &&
      customerField.getValue() &&
      value?.customer?.id !== customerField.getValue()?.id
    ) {
      setShowMismatchedCustomerError(true)
    } else {
      setShowMismatchedCustomerError(false)
    }
  }
  useEffect(() => {
    const unhook = customerField.events.on(Form2.fieldEvents.change, () => {
      checkOrderCustomer()
    })
    return () => {
      unhook()
    }
  }, [value?.id])

  useEffect(() => {
    checkOrderCustomer()
  }, [value?.id])

  const debouncedQuery = useStable(inputValue)

  useAsyncEffect(async () => {
    if (!hasMountedRef.current) {
      return
    }
    await handleLookupOptions("")
  }, [customerField.getValue()?.id, product?.id])

  useAsyncEffect(async () => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    const query = debouncedQuery
    if (!query) {
      setShowMismatchedCustomerError(false)
      return
    }
    await handleLookupOptions(debouncedQuery)
  }, [debouncedQuery])

  const [orderOptions, setOrderOptions] = React.useState<IRmaItemOrder[]>(value ? [value] : [])

  async function handleLookupOptions(query: string) {
    setIsLoading(true)
    try {
      const customer = customerField.getValue()

      const identifier = removeLeadingZeros(query)

      const orderFilter = {
        and: [
          { matches: { branch: branchField.getValue()?.id, customer: customer?.id } },
          product && {
            gt: [
              {
                count: {
                  path: ["items", { name: "product", filter: { matches: { id: product.id } } }],
                },
              },
              { value: 0 },
            ],
          },
          {
            or: [
              // invoice number - orders have invoice items, these have invoice_document_number
              {
                gt: [
                  {
                    count: {
                      path: [
                        {
                          name: "invoice_items",
                          filter: { matches: { invoice_document_number: { like: identifier } } },
                        },
                      ],
                    },
                  },
                  { value: 0 },
                ],
              },
              // Delivery Note number - this is order identifier
              { matches: { identifier: { like: identifier } } },
              // Customer Order Number - this is the purchase order ref
              { matches: { purchase_order_reference: { like: identifier } } },
            ],
          } as any,
        ].filter(Boolean),
      }

      const orders = await skeem.fetch2<IRmaItemOrder[]>({
        model: {
          name: "orders",
          attributes: [
            "identifier",
            "purchase_order_reference",
            {
              as: "invoice_number",
              value: {
                first: {
                  path: [
                    {
                      name: "invoice_items",
                      filter: { matches: { invoice_document_number: { not: "" } } },
                    },
                    "invoice_document_number",
                  ],
                },
              },
            },
            { as: "invoice_date", value: { first: { path: ["invoice_items", "invoice_date"] } } },
            { name: "delivery", attributes: ["confirmed_at"] },
            { name: "customer" },
          ],
          filter: orderFilter,
        },
      })

      // if we got a single match, select it (e.g 0001234 -> 1234)
      setOrderOptions(orders)
    } finally {
      setIsLoading(false)
    }
  }

  function handleChange(option?: IRmaItemOrder) {
    onChange(option)
    checkOrderCustomer()
  }
  if (value) {
    return (
      <div>
        <div
          css={css`
            padding: 8px;
            border: 1px solid ${colors.lightBorder};
            border-radius: 3px;
            background-color: white;
          `}
        >
          <Grid columns={["auto", "max-content"]} gap={8}>
            <H4>Order</H4>
            <a
              css={css`
                cursor: pointer;
              `}
              onClick={() => {
                onChange(undefined)
              }}
            >
              clear
            </a>
          </Grid>

          <div>
            <KeyValueList
              gap={4}
              data={[
                { label: "Invoice Number", value: value.invoice_number, returnNullOnEmpty: true },
                {
                  label: "Delivery Note Number",
                  value: value.identifier,
                  returnNullOnEmpty: true,
                },
                {
                  label: "Customer Order Number",
                  value: value.purchase_order_reference,
                  returnNullOnEmpty: true,
                },
              ]}
              inline
            />
          </div>
        </div>
        {showMismatchedCustomerError && !customerView && (
          <P color="red">This order does not belong to the selected customer.</P>
        )}
      </div>
    )
  }

  return (
    <Grid gap={2}>
      <Fields.Select2
        label={<Label optional>Order</Label>}
        onInputChange={setInputValue}
        inputValue={inputValue}
        error={error}
        options={orderOptions}
        onChange={handleChange}
        getOptionLabel={formatLabel}
        getOptionValue={opt => opt.id}
        isLoading={isLoading}
        value={value}
        // overide the select search as we are already filtering our options down
        filterOption={() => true}
        isClearable
      />
      {showMismatchedCustomerError && !customerView && (
        <div>
          <P color="red">This order does not belong to the selected customer.</P>
        </div>
      )}
    </Grid>
  )
}

export function Label({
  children,
  required,
  optional,
}: {
  children: React.ReactNode
  required?: boolean
  optional?: boolean
}) {
  return (
    <span
      css={css`
        font-weight: 400;
        font-size: 13px;
        color: #4a4e69;
      `}
    >
      {children}
      {required && (
        <span
          css={css`
            color: red;
            margin-left: 4px;
          `}
        >
          *
        </span>
      )}
      {optional && (
        <span
          css={css`
            color: #999;
            margin-left: 4px;
            font-size: 0.8em;
          `}
        >
          (optional)
        </span>
      )}
    </span>
  )
}

function RmaSubCustomerSelectList({ customerId }: { customerId?: string }) {
  const subCustomerField = Form2.useField<IRmaSubCustomer | undefined>("sub_customer", {
    subscribe: true,
  })
  const [subCustomers, setSubCustomers] = React.useState<IRmaSubCustomer[]>([])

  async function loadSubCustomers() {
    if (!customerId) {
      setSubCustomers([])
      subCustomerField.setValue(undefined)
      return
    }
    const subCustomers = await window.skeem.fetch2<IRmaSubCustomer[]>({
      model: {
        name: "sub_customers",
        attributes: rmaSubCustomerAttributes,
        filter: { matches: { customer: customerId } },
      },
    })
    setSubCustomers(subCustomers)
  }

  useAsyncEffect(loadSubCustomers, [customerId])

  if (!customerId) {
    return null
  }

  if (subCustomers.length === 0) {
    return null
  }

  return (
    <div key={customerId} css={toolbarItemWidth}>
      <Form2.Select
        options={subCustomers}
        field="sub_customer"
        label="Sub Customer"
        getOptionLabel={opt => opt.name}
        getOptionValue={opt => opt.id}
        isClearable
      />
    </div>
  )
}

export type IRmaNotificationStatus = "approved" | "rejected"

/**
 * The PDFs the server attaches to an RMA notification for a given status.
 * Keep in sync with the attachment logic in server/routers/rmaRouter.ts.
 */
export function rmaNotificationPdfs(rmaId: string, status: IRmaNotificationStatus) {
  if (status === "rejected") {
    return [{ label: "RMA Rejected PDF", url: `/pdfs/rma/rejected/${rmaId}` }]
  }
  return [
    { label: "How to return goods PDF", url: `/pdfs/rma/approved/${rmaId}` },
    { label: "RMA confirmation PDF", url: `/pdfs/rma/${rmaId}` },
  ]
}

function mapRmaStatusToNotificationStatus(
  status?: RmaStatus | string,
): IRmaNotificationStatus | undefined {
  if (!status) {
    return undefined
  }
  return status === RmaStatus.REJECTED ? "rejected" : "approved"
}

function rmaNotificationRecipientReasonLabel(reason: string): string | null {
  switch (reason) {
    case "contact":
      return "This recipient was chosen because the RMA has a linked contact."
    case "created_by_customer_user":
      return "This recipient was chosen because no contact is linked to the RMA, and the person who created it is a user on this customer account."
    case "default_contact":
      return "This recipient was chosen because no contact is linked and the creator is not a customer user for this account, so the customer's default contact is used."
    case "internal_customer_created_by_user":
      return "This recipient was chosen because this is an internal customer account: the person who created the RMA is notified instead of the customer's default contact."
    case "created_by_user":
      return "This recipient was chosen as a fallback: no linked contact or default contact was found, so the creating user's email is used."
    default:
      return null
  }
}

function SendRmaEmail({
  rmaId,
  defaultStatus,
}: {
  rmaId: string
  defaultStatus?: IRmaNotificationStatus
}) {
  const handleSendRmaEmail = useHandleSendRmaEmail()
  return (
    <BaseButton
      {...buttonConfigs.generalMint}
      renderLeft={() => <Icon icon={icons.sentEmail} color={brandColors.mintPop} />}
      type="button"
      onClick={() => handleSendRmaEmail(rmaId, defaultStatus)}
    >
      Send RMA Email
    </BaseButton>
  )
}

export function useHandleSendRmaEmail(opts?: { skippable?: boolean }) {
  const api = useApi()
  const alerts = useSiteAlerts()
  const downloadFile = useDownload()
  const { currentUser } = useEnsureGlobalState()
  async function handleSendRmaEmail(
    rmaId: string,
    defaultStatus: IRmaNotificationStatus = "approved",
  ) {
    const selectedStatus = await handleSelectNotificationStatus(defaultStatus)
    if (!selectedStatus) {
      return
    }

    const emailData = await api.rmaNotificationDetails(rmaId, selectedStatus)
    if (emailData.error) {
      alerts.genericError("Failed to load email data")
      console.error(emailData)
      return
    }
    let recipientUser: IRecord<"skeem_users", "skeem_email"> | undefined = undefined
    if (emailData.recipient.userIds?.length) {
      recipientUser = await skeem.fetch2<IRecord<"skeem_users", "skeem_email">>({
        first: {
          name: "skeem_users",
          matches: { id: emailData.recipient.userIds[0] },
          attributes: ["skeem_email"],
        },
      })
    }
    const emailDataToUse = {
      rmaId,
      status: selectedStatus,
      recipient: {
        user: emailData.recipient.userIds?.length ? true : false,
        email: recipientUser?.skeem_email || emailData.recipient.emails?.[0] || "",
      },
      subject: emailData.subject,
      body: emailData.body,
      recipientReason: emailData.recipientReason ?? "",
      includeAttachments: true,
    }
    const updatedEmailData = await handleManageEmailContent(emailDataToUse)
    if (!updatedEmailData) {
      return
    }

    const recipient = emailData.recipient
    if (!updatedEmailData.recipient.user) {
      recipient.emails = [updatedEmailData.recipient.email]
      recipient.userIds = []
    }

    const finalData = {
      recipient,
      subject: updatedEmailData.subject,
      body: updatedEmailData.body,
      status: selectedStatus,
      includeAttachments: updatedEmailData.includeAttachments,
      senderId: currentUser.id,
    }
    try {
      const sent = await api.rmaNotificationOverride(rmaId, finalData)
      if (sent.error) {
        throw new Error(`Failed to send email - ${sent.error ?? "Unknown error"}`)
      }
      alerts.genericSuccess("Email sent successfully")
    } catch (e) {
      alerts.genericError(e instanceof Error ? e.message : "Failed to send email")
      console.error(e)
    }
  }

  const handleSelectNotificationStatus = useManagedDialog<
    IRmaNotificationStatus | null,
    IRmaNotificationStatus
  >({
    Component: ({ dialogData, onResolve }) => {
      const [status, setStatus] = useState<IRmaNotificationStatus>(dialogData)

      return (
        <div>
          <H3>Select Email Type</H3>
          <P size="small">
            Choose which notification to prepare before reviewing the email content.
          </P>
          <Spacer />
          <Grid gap={8}>
            <FieldsRadioCheckbox
              label="Approved"
              value={status === "approved"}
              onChange={checked => {
                if (checked) {
                  setStatus("approved")
                }
              }}
              name="rma-notification-status"
            />
            <FieldsRadioCheckbox
              label="Rejected"
              value={status === "rejected"}
              onChange={checked => {
                if (checked) {
                  setStatus("rejected")
                }
              }}
              name="rma-notification-status"
            />
          </Grid>
          <Spacer />
          <ConfirmCancelButtons
            onConfirm={() => onResolve(status)}
            onCancel={() => onResolve(null)}
            buttonOpts={{
              confirm: { children: "Continue" },
              cancel: opts?.skippable ? { children: "Skip" } : undefined,
            }}
          />
        </div>
      )
    },
  })

  const handleManageEmailContent = useManagedDialog<
    {
      recipient: {
        user: boolean
        email: string
      }
      subject: string
      body: string
      recipientReason: string
      includeAttachments: boolean
    } | null,
    {
      rmaId: string
      status: IRmaNotificationStatus
      recipient: { user: boolean; email: string }
      subject: string
      body: string
      recipientReason: string
      includeAttachments: boolean
    }
  >({
    Component: ({ dialogData, onResolve }) => {
      const [data, setData] = useState(dialogData)
      const [errors, setErrors] = useState<{ recipient?: string; subject?: string; body?: string }>(
        {},
      )
      function setPartialState(partialState: Partial<typeof dialogData>) {
        setData(old => ({ ...old, ...partialState }))
      }
      function validate() {
        const newErrors: typeof errors = {}
        // if not using user - validate the email is present
        if (!data.recipient.user) {
          if (!data.recipient.email) {
            newErrors.recipient = "Recipient email is required"
          }
        }
        if (!data.subject) {
          newErrors.subject = "Subject is required"
        }
        if (!data.body) {
          newErrors.body = "Body is required"
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
      }
      const recipientReasonText = rmaNotificationRecipientReasonLabel(dialogData.recipientReason)
      return (
        <div>
          <H3>Manage Email</H3>
          {recipientReasonText ? (
            <div>
              <P size="small" css={{ fontStyle: "italic" }}>
                {recipientReasonText}
              </P>
              <Spacer size={8} />
            </div>
          ) : null}
          <Grid
            columns={
              [{ fr: 1 }, dialogData.recipient.user ? "max-content" : undefined].filter(
                Boolean,
              ) as any
            }
            gap={8}
          >
            {data.recipient.user ? (
              <div>
                <Fields.Email
                  label="Recipient"
                  value={data.recipient.email}
                  error={errors.recipient}
                  showRequiredStar
                  disabled
                />
                <P size="small">
                  A user was matched as the recipient - the actual email to receive will be based on
                  the users settings. If you want to change the recipient email, click edit and
                  enter the desired email address.
                </P>
              </div>
            ) : (
              <Fields.Email
                label="Recipient"
                value={data.recipient.email}
                onChange={e => setPartialState({ recipient: { user: false, email: e } })}
                error={errors.recipient}
                showRequiredStar
              />
            )}
            {dialogData.recipient.user ? (
              <div
                css={css`
                  margin-top: 16px;
                `}
              >
                <Button
                  leftIcon={data.recipient.user ? icons.edit : icons.user}
                  onClick={() => {
                    if (data.recipient.user) {
                      setPartialState({ recipient: { user: false, email: data.recipient.email } })
                    } else {
                      setPartialState({
                        recipient: { user: true, email: dialogData.recipient.email },
                      })
                    }
                  }}
                  buttonColor={data.recipient.user ? undefined : "blue"}
                >
                  {data.recipient.user ? "Edit Recipient" : "Use Matched User"}
                </Button>
              </div>
            ) : null}
          </Grid>

          <Spacer />
          <Fields.TextArea
            label="Subject"
            onChange={e => setPartialState({ subject: e })}
            value={data.subject}
            error={errors.subject}
            showRequiredStar
          />
          <Spacer />
          <Fields.TextArea
            label="Body"
            onChange={e => setPartialState({ body: e })}
            value={data.body}
            error={errors.body}
            showRequiredStar
          />
          <Spacer />
          <Fields.Checkbox
            label="Include attachments"
            value={data.includeAttachments}
            onChange={checked => setPartialState({ includeAttachments: checked })}
          />
          <Spacer size={8} />
          {data.includeAttachments ? (
            <div>
              <P>
                The following PDFs will be automatically attached based on the previously selected
                status. Click one to view it.
              </P>
              <Spacer size={8} />
              <ButtonsWrapper>
                {rmaNotificationPdfs(dialogData.rmaId, dialogData.status).map(pdf => (
                  <Button
                    key={pdf.url}
                    buttonColor={themeColors.pdfRed}
                    leftIcon={icons.pdf}
                    rightIcon={icons.external}
                    onClick={() => downloadFile(pdf.url)}
                  >
                    {pdf.label}
                  </Button>
                ))}
              </ButtonsWrapper>
            </div>
          ) : (
            <P>No PDFs will be attached.</P>
          )}
          <Spacer />
          <ConfirmCancelButtons
            onConfirm={() => {
              if (!validate()) {
                return
              }
              if (confirm("Are you sure you want to send this email?")) {
                onResolve(data)
              }
            }}
            onCancel={() => onResolve(null)}
            buttonOpts={{
              confirm: { children: "Send Email", leftIcon: icons.bulkEmail },
              cancel: opts?.skippable ? { children: "Skip" } : undefined,
            }}
          />
        </div>
      )
    },
  })

  return handleSendRmaEmail
}
